import React, { useEffect, useState } from "react";
import Calendar, { CalendarTileProperties } from "react-calendar";
import IconCalendar from "../../Icons/Report/Calendar.svg";
import DateUtil from "../../Utility/DateUtil";
import MtiButton from "../Buttons/MtiButton";
import Modal from "../Common/Modal";
import "./DateFilter.css";
import "./DateFilterCalendar.css";
import MonthPicker from "./MonthPicker";

export type Props = {
  dateRange: { minDate: Date; maxDate: Date };
  setDateRange: (dateRange: { minDate: Date; maxDate: Date }) => void;
};

function getLabelDisplay(dateRange: { minDate: Date; maxDate: Date }) {
  return (
    DateUtil.toUsDateOnlyFormatString(dateRange.minDate) +
    "-" +
    DateUtil.toUsDateOnlyFormatString(dateRange.maxDate)
  );
}

export default function DateFilter({ dateRange, setDateRange }: Props) {
  const MIN_DATE = new Date(1900, 1);
  const MAX_DATE = new Date(3000, 1);
  const [currentDateRange, setCurrentDateRange] = useState(dateRange);
  const [labelDisplay, setLabelDisplay] = useState(getLabelDisplay(dateRange));
  const { minDate, maxDate } = currentDateRange;
  const [showCalender, setShowCalendar] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState<{
    start: boolean;
    end: boolean;
  }>({ start: false, end: false });
  const [selectedMonth, setSelectedMonth] = useState<{
    start: Date;
    end: Date;
  }>({ start: new Date(), end: new Date() });

  useEffect(() => {
    setCurrentDateRange(dateRange);
  }, [dateRange]);

  const onSelectedMonthChanged = (month: Date, type: "start" | "end") => {
    setSelectedMonth({
      ...selectedMonth,
      [type]: month,
    });
    setShowMonthPicker({ start: false, end: false });
  };

  const onDateRangeChanged = (dateRange: { minDate: Date; maxDate: Date }) => {
    setCurrentDateRange(dateRange);
  };

  const getLabel = (type: "start" | "end") => {
    return (
      <div className={"date-filter-label " + type}>
        <label>{type == "start" ? "Start Date:" : "End Date:"}</label>
      </div>
    );
  };

  const getTileClassName = (props: CalendarTileProperties) => {
    if (
      DateUtil.isSameDay(props.date, currentDateRange.minDate) ||
      DateUtil.isSameDay(props.date, currentDateRange.maxDate)
    ) {
      return "date-filter-calendar-active-tile";
    }

    const time = props.date.getTime();
    if (time > minDate.getTime() && time < maxDate.getTime()) {
      return "date-filter-calendar-selected-tile";
    }

    return "";
  };

  const getContent = (
    date: Date,
    onChangeDate: (date: Date) => void,
    type: "start" | "end"
  ) => {
    return (
      <div className={"date-filter-content " + type}>
        <Calendar
          calendarType={"US"}
          //value={date}
          tileClassName={getTileClassName}
          className={["date-filter-calendar"]}
          onChange={(date) => {
            onChangeDate(date as Date);
          }}
          maxDate={type === "start" ? currentDateRange.maxDate : MAX_DATE}
          minDate={type === "start" ? MIN_DATE : currentDateRange.minDate}
          showNavigation={false}
          formatShortWeekday={(locale, date) =>
            date.toLocaleDateString("en-us", { weekday: "short" })[0]
          }
          value={type === "start" ? selectedMonth.start : selectedMonth.end}
        />
      </div>
    );
  };

  const getMonthPicker = (type: "start" | "end") => {
    return (
      <div className={"date-filter-month-picker " + type}>
        <MonthPicker
          selectedMonth={
            type === "start" ? selectedMonth.start : selectedMonth.end
          }
          setSelectedMonth={(selectedMonth) =>
            onSelectedMonthChanged(selectedMonth, type)
          }
          yearRange={{
            minYear: type === 'start' ? 2019 : selectedMonth.start.getFullYear(),
            maxYear: DateUtil.getCurrentYear(),
          }}
          startEndSelected={selectedMonth}
          type={type}
        />
      </div>
    );
  };

  const validatePickMonthAction = (isNextMonth: boolean, type: "start" | "end") => {
    let year = selectedMonth[type].getFullYear();
    let month = selectedMonth[type].getMonth();

    const date = new Date(year, month);

    let canChange = false;

    if(isNextMonth) {
      if (type === "end") {
        canChange = true;
      } else {
        if (
          date.getFullYear() <= maxDate.getFullYear() &&
          date.getMonth() < maxDate.getMonth()
        ) {
          canChange = true;
        }
      }
    } else {
      if (type === "start") {
        canChange = true;
      } else {
        if (
          date.getFullYear() >= minDate.getFullYear() &&
          date.getMonth() > minDate.getMonth()
        ) {
          canChange = true;
        }
      }
    }

    return canChange
  }

  const onPreviousMonthClicked = (type: "start" | "end") => {
    let year = selectedMonth[type].getFullYear();
    let month = selectedMonth[type].getMonth();
    if (month === 0) {
      month = 11;
      year--;
    } else {
      month--;
    }

    const date = new Date(year, month);


    if (validatePickMonthAction(false, type)) {
      setSelectedMonth({
        ...selectedMonth,
        [type]: date,
      });
    }
  };

  const onNextMonthClicked = (type: "start" | "end") => {
    let year = selectedMonth[type].getFullYear();
    let month = selectedMonth[type].getMonth();
    if (month === 11) {
      month = 0;
      year++;
    } else {
      month++;
    }

    const date = new Date(year, month);


    if (validatePickMonthAction(true, type)) {
      setSelectedMonth({
        ...selectedMonth,
        [type]: date,
      });
    }
  };

  const getMonth = (type: "start" | "end") => {
    return (
      <div className={"date-filter-month " + type}>
        <div
          className="date-filter-month-label"
          onClick={() => {
            if (type === "start") {
              setShowMonthPicker({ start: !showMonthPicker.start, end: false });
            } else {
              setShowMonthPicker({ start: false, end: !showMonthPicker.end });
            }
          }}
        >
          <label>
            {DateUtil.getMonthYearName(
              type === "start" ? selectedMonth.start : selectedMonth.end
            )}
          </label>
          {((type === "start" && !showMonthPicker.start) ||
            (type == "end" && !showMonthPicker.end)) && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.99999 12L1.33332 4.76667L14.6667 4.76667L7.99999 12Z"
                fill="#474747"
              />
            </svg>
          )}
          {((type === "start" && showMonthPicker.start) ||
            (type === "end" && showMonthPicker.end)) && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: "rotate(180deg)",
              }}
            >
              <path
                d="M7.99999 12L1.33332 4.76667L14.6667 4.76667L7.99999 12Z"
                fill="#474747"
              />
            </svg>
          )}
        </div>
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          onClick={() => onPreviousMonthClicked(type)}
        >
          <path
            d="M16.5575 10.5575L15.5 9.5L11 14L15.5 18.5L16.5575 17.4425L13.1225 14L16.5575 10.5575Z"
            fill={validatePickMonthAction(false, type) ? "#313131" : "#b4b4b4"}
          />
          <path
            d="M16.5575 10.5575L15.5 9.5L11 14L15.5 18.5L16.5575 17.4425L13.1225 14L16.5575 10.5575Z"
            fill={validatePickMonthAction(false, type) ? "#313131" : "#b4b4b4"}
          />
        </svg>
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          onClick={() => onNextMonthClicked(type)}
        >
          <path
            d="M12.5 9.5L11.4425 10.5575L14.8775 14L11.4425 17.4425L12.5 18.5L17 14L12.5 9.5Z"
            fill={validatePickMonthAction(true, type) ? "#313131" : "#b4b4b4"}
          />
          <path
            d="M12.5 9.5L11.4425 10.5575L14.8775 14L11.4425 17.4425L12.5 18.5L17 14L12.5 9.5Z"
            fill={validatePickMonthAction(true, type) ? "#313131" : "#b4b4b4"}
          />
        </svg>
      </div>
    );
  };

  const onOk = () => {
    setShowCalendar(false);
    setDateRange(currentDateRange);
    setLabelDisplay(getLabelDisplay(currentDateRange));
  };

  const onCancel = () => {
    setShowCalendar(false);
    setCurrentDateRange(dateRange);
    setSelectedMonth({ start: dateRange.minDate, end: dateRange.maxDate });
  };

  const daysDiff =
    DateUtil.dateDiffInDays(
      currentDateRange.minDate,
      currentDateRange.maxDate
    ) + 1;

  const daysDiffString =
    daysDiff + (daysDiff > 1 ? " days" : " day") + " selected";

  return (
    <div className="date-filter">
      <input
        readOnly={true}
        value={labelDisplay}
        style={{
          paddingRight: 28,
          background: `url(${IconCalendar}) 98% / 24px no-repeat, #FFFFFF`,
        }}
        onClick={() => setShowCalendar(true)}
      />
      <Modal
        show={showCalender}
        onHide={() => setShowCalendar(false)}
        style={{ borderRadius: 0 }}
        className="date-filter-calendar-modal"
        classNameBackdrop="date-filter-calendar-modal-backdrop"
        relativeToParent={true}
      >
        <Modal.Body>
          <div className="date-filter-container">
            {getLabel("start")}
            {getLabel("end")}
            {getMonth("start")}
            {getMonth("end")}
            {!showMonthPicker.start &&
              getContent(
                minDate,
                (date) => {
                  onDateRangeChanged({ minDate: date, maxDate: maxDate });
                },
                "start"
              )}
            {!showMonthPicker.end &&
              getContent(
                dateRange.maxDate,
                (date) => {
                  onDateRangeChanged({ minDate: minDate, maxDate: date });
                },
                "end"
              )}
            <div className="date-filter-footer start">
              <label>{daysDiffString}</label>
            </div>
            <div className="date-filter-footer end">
              <MtiButton onClick={onOk}>OK</MtiButton>
              <MtiButton onClick={onCancel} variant="secondary">
                Cancel
              </MtiButton>
            </div>
            {showMonthPicker.start && getMonthPicker("start")}
            {showMonthPicker.end && getMonthPicker("end")}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
